import React, { useState, useEffect, useCallback } from 'react';
import { X, User, Building2, Phone, Mail, AlertCircle, Flag, Briefcase, FileText, Package, Pause, Play, Calendar, Eye, Layout, Clipboard, MessageSquare, RefreshCw, Loader2 } from 'lucide-react';
import { SearchService } from '../services/searchService';
import { ProjectService } from '../services/projectService';
import { StatusValidationService } from '../services/statusValidationService';
import { DiscrepancyService } from '../services/discrepancyService';
import { Modal, ModalBody, ModalFooter, Button } from './ui';
import HoldProjectModal from './HoldProjectModal';
import FullSubitemModal from './FullSubitemModal';
import StatusAdvancementWarningModal from './StatusAdvancementWarningModal';
import ModelPlannerWizardModal from './ModelPlanner/ModelPlannerWizardModal';
import DiscrepancyModal from './Discrepancies/DiscrepancyModal';
import DiscrepancyDetailsModal from './DiscrepancyDetailsModal';
import { formatJobRevNumber } from '../utils/projectFormatting';

// Helper function to check if project can have subitem view
const canViewSubitems = (project) => {
  // Don't show for completed projects
  if (project?.modelingStatus === 'Completed') return false;

  // Don't show for projects on hold
  if (project?.modelingState === 'Hold') return false;

  // Show for all other active non-completed projects
  return true;
};

// Helper function to get discrepancy status color
const getDiscrepancyStatusColor = (status) => {
  const statusColors = {
    'Draft': '#6b7280',
    'Waiting to Review': '#f59e0b',
    'Sent': '#3b82f6',
    'Reviewed - not sent': '#8b5cf6',
    'Resolved': '#10b981'
  };
  return statusColors[status] || '#6b7280';
};

const ProjectDetailsModal = ({ project: initialProject, isOpen, onClose, onProjectUpdate }) => {
  const [project, setProject] = useState(initialProject);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [removingHold, setRemovingHold] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showSubitemModal, setShowSubitemModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showModelPlannerModal, setShowModelPlannerModal] = useState(false);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [prefilledDiscrepancy, setPrefilledDiscrepancy] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);
  const [showDiscrepancyDetailsModal, setShowDiscrepancyDetailsModal] = useState(false);
  const [selectedDiscrepancyId, setSelectedDiscrepancyId] = useState(null);
  const [relatedRevisions, setRelatedRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [warningData, setWarningData] = useState({
    incompleteSubitems: [],
    currentStatus: '',
    targetStatus: '',
    pendingStatus: null
  });
  const [refreshingFiles, setRefreshingFiles] = useState(false);

  const handleBringFilesBack = async () => {
    setRefreshingFiles(true);
    try {
      await ProjectService.updateProjectBatch(project.id, { stage_modified_refresh: false });
      await ProjectService.updateProjectBatch(project.id, { stage_modified_refresh: true });
    } catch (err) {
      console.error('Error refreshing project files:', err);
    } finally {
      setRefreshingFiles(false);
    }
  };

  const fetchProjectDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await SearchService.getProjectDetails(initialProject.id);

    setLoading(false);

    if (fetchError) {
      setError(fetchError);
    } else if (data) {
      // Transform hold data if exists
      const transformedData = {
        ...data,
        holdStartDate: data.hold_start_date,
        holdReason: data.hold_reason,
        holdNotes: data.hold_notes,
        holdPriority: data.hold_priority,
        holdInitialFollowUpDays: data.hold_initial_follow_up_days,
        holdRecurringFollowUpDays: data.hold_recurring_follow_up_days,
        holdAdditionalContacts: data.hold_additional_contacts,
        holdReminderSentDates: data.hold_reminder_sent_dates
      };
      // Calculate next follow-up date if on hold
      if (data.modeling_state === 'Hold' && data.hold_start_date) {
        transformedData.nextFollowUpDate = calculateNextFollowUp(
          data.hold_start_date,
          data.hold_initial_follow_up_days,
          data.hold_recurring_follow_up_days,
          data.hold_reminder_sent_dates
        );
      }
      setProject(transformedData);
    }
  }, [initialProject]);

  const fetchDiscrepancies = useCallback(async () => {
    if (!initialProject?.id) return;

    setLoadingDiscrepancies(true);
    try {
      const discrepancyData = await DiscrepancyService.getDiscrepanciesByProjectId(initialProject.id);
      setDiscrepancies(discrepancyData || []);
    } catch (err) {
      console.error('Error fetching discrepancies:', err);
      setDiscrepancies([]);
    } finally {
      setLoadingDiscrepancies(false);
    }
  }, [initialProject]);

  const fetchRelatedRevisions = useCallback(async () => {
    if (!project?.jobNumber) return;

    setLoadingRevisions(true);
    try {
      const { data } = await SearchService.getRelatedRevisions(project.jobNumber, project.id);
      setRelatedRevisions(data || []);
    } catch (err) {
      console.error('Error fetching related revisions:', err);
      setRelatedRevisions([]);
    } finally {
      setLoadingRevisions(false);
    }
  }, [project?.jobNumber, project?.id]);

  // Fetch full project details when modal opens (no real-time updates)
  useEffect(() => {
    if (isOpen && initialProject?.id) {
      fetchProjectDetails();
      fetchDiscrepancies();
    }
  }, [isOpen, initialProject?.id, fetchProjectDetails, fetchDiscrepancies]);

  // Fetch related revisions after project details are loaded
  useEffect(() => {
    if (isOpen && project?.jobNumber) {
      fetchRelatedRevisions();
    }
  }, [isOpen, project?.jobNumber, fetchRelatedRevisions]);

  const calculateNextFollowUp = (holdStartDate, initialDays, recurringDays, remindersSent) => {
    if (!holdStartDate || !initialDays) return null;

    const startDate = new Date(holdStartDate);

    if (!remindersSent || remindersSent.length === 0) {
      // First follow-up
      const followUpDate = new Date(startDate);
      followUpDate.setDate(followUpDate.getDate() + initialDays);
      return followUpDate.toISOString();
    } else {
      // Recurring follow-up
      const lastReminder = new Date(remindersSent[remindersSent.length - 1]);
      const followUpDate = new Date(lastReminder);
      followUpDate.setDate(followUpDate.getDate() + (recurringDays || 7));
      return followUpDate.toISOString();
    }
  };

  const handleRemoveFromHold = async () => {
    setRemovingHold(true);
    try {
      await ProjectService.updateProjectBatch(project.id, {
        modeling_state: 'Active'
      });
      await fetchProjectDetails();

      // Notify parent component about the update if callback exists
      if (onProjectUpdate && project) {
        onProjectUpdate({ ...project, modelingState: 'Active' });
      }
    } catch (err) {
      console.error('Error removing from hold:', err);
      setError('Failed to remove project from hold');
    } finally {
      setRemovingHold(false);
    }
  };

  const handleHoldSuccess = async () => {
    await fetchProjectDetails();

    // Notify parent component that project is on hold
    if (onProjectUpdate && project) {
      onProjectUpdate({ ...project, modelingState: 'Hold' });
    }
  };

  const formatHoldReason = (reason) => {
    const reasonMap = {
      'waiting_updated_pdfs': 'Waiting on Updated PDFs',
      'waiting_updated_cad': 'Waiting on Updated CAD Files',
      'waiting_discrepancy_clarification': 'Waiting on Discrepancy Clarification',
      'client_requested_hold': 'Client Requested Hold'
    };
    return reasonMap[reason] || reason;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysOnHold = (holdStartDate) => {
    if (!holdStartDate) return 0;
    const start = new Date(holdStartDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isOverdue = (followUpDate) => {
    if (!followUpDate) return false;
    return new Date(followUpDate) < new Date();
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (!newStatus || newStatus === project.modelingStatus) {
      return;
    }

    setUpdatingStatus(true);

    try {
      // Validate status change
      const validation = await StatusValidationService.validateStatusChange(
        project.id,
        project.modelingStatus,
        newStatus
      );

      if (!validation.isValid && validation.incompleteSubitems.length > 0) {
        // Show warning modal
        setWarningData({
          incompleteSubitems: validation.incompleteSubitems,
          currentStatus: project.modelingStatus || 'None',
          targetStatus: newStatus,
          pendingStatus: newStatus
        });
        setShowWarningModal(true);
        setUpdatingStatus(false);
        return;
      }

      // Proceed with the update
      await executeStatusUpdate(newStatus);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update modeling status');
      setUpdatingStatus(false);
    }
  };

  const executeStatusUpdate = async (newStatus) => {
    try {
      await ProjectService.updateProject(project.id, 'modelingStatus', newStatus);

      // Refetch project details to get any auto-updated fields (like modeling_state from triggers)
      await fetchProjectDetails();

      // Notify parent component that it should refetch the project
      if (onProjectUpdate) {
        // Fetch the updated project data to ensure parent has latest state
        const { data } = await SearchService.getProjectDetails(project.id);
        if (data) {
          const transformedData = {
            ...data,
            holdStartDate: data.hold_start_date,
            holdReason: data.hold_reason,
            holdNotes: data.hold_notes,
            holdPriority: data.hold_priority,
            holdInitialFollowUpDays: data.hold_initial_follow_up_days,
            holdRecurringFollowUpDays: data.hold_recurring_follow_up_days,
            holdAdditionalContacts: data.hold_additional_contacts,
            holdReminderSentDates: data.hold_reminder_sent_dates
          };
          onProjectUpdate(transformedData);
        }
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update modeling status');
      throw err;
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleOverrideWarning = async () => {
    setShowWarningModal(false);
    setUpdatingStatus(true);
    try {
      await executeStatusUpdate(warningData.pendingStatus);
    } catch (err) {
      // Error already handled in executeStatusUpdate
    }
    setWarningData({ incompleteSubitems: [], currentStatus: '', targetStatus: '', pendingStatus: null });
  };

  const handleCloseWarning = () => {
    setShowWarningModal(false);
    setWarningData({ incompleteSubitems: [], currentStatus: '', targetStatus: '', pendingStatus: null });
  };

  const handleOpenDiscrepancyModal = (projectData) => {
    // Create prefilled discrepancy with project data
    const prefilled = {
      projectId: projectData?.id || null,
      discrepancyType: '',
      discrepancyStatus: 'Draft',
      description: '',
      loomLink: '',
      markupAttachment: [],
      additionalInformation: '',
      additionalContactIds: [],
      createdBy: '',
      sentDate: null,
      emailPreview: '',
      emailHtml: '',
      emailTo: [],
      emailCc: [],
      emailSubject: '',
      sendEmail: '',
      putProjectOnHold: 'no',
      holdReason: 'waiting_discrepancy_clarification'
    };
    setPrefilledDiscrepancy(prefilled);
    setShowDiscrepancyModal(true);
  };

  const handleDiscrepancySave = async (discrepancy) => {
    // Discrepancy saved successfully
    setShowDiscrepancyModal(false);
    setPrefilledDiscrepancy(null);
    // Optionally refresh project details if needed
  };

  const handleDiscrepancyClose = () => {
    setShowDiscrepancyModal(false);
    setPrefilledDiscrepancy(null);
  };

  if (!project) return null;

  // Debug logging to check data
  console.log('ProjectDetailsModal - project data:', {
    jobNumber: project?.jobNumber,
    rev: project?.rev,
    projectName: project?.projectName,
    name: project?.name,
    fullProject: project
  });

  return (
    <>
      <Modal trackAs="ProjectDetailsModal" isOpen={isOpen} onClose={onClose} size="xl" showCloseButton={false} theme="dark">
        {/* Custom Header with Project Info */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            {/* Job Number and Project Name on one line - Match FullSubitemModal */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
              <span
                style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-400)',
                  flexShrink: 0,
                }}
              >
                {formatJobRevNumber(project?.jobNumber, project?.rev)}
              </span>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {project?.projectName || 'Loading...'}
              </h1>
              {project?.isHighPriority && (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 'var(--radius-full)',
                    color: '#fca5a5',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    flexShrink: 0,
                  }}
                >
                  <Flag size={12} />
                  High Priority
                </div>
              )}
            </div>

            {/* View Subitems Button */}
            {canViewSubitems(project) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowSubitemModal(true)}
              >
                <Eye size={14} />
                View Subitems
              </Button>
            )}

            {/* Close Button */}
            <button
              onClick={onClose}
              aria-label="Close modal"
              style={{
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: 'var(--gray-400)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--gray-700)';
                e.target.style.color = 'var(--gray-200)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'var(--gray-400)';
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <ModalBody>
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 24px',
              gap: 'var(--space-4)'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid var(--gray-600)',
                borderTopColor: 'var(--cta-blue)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ margin: 0, color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>
                Loading project details...
              </p>
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 24px',
              gap: 'var(--space-4)',
              textAlign: 'center'
            }}>
              <AlertCircle size={32} style={{ color: 'var(--danger)' }} />
              <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-200)' }}>
                Error loading project details
              </h3>
              <p style={{ margin: 0, color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>{error}</p>
            </div>
          ) : (
            /* Two column layout */
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
              {/* LEFT COLUMN */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Project Status Section */}
                <div>
                  <h2
                    style={{
                      margin: 0,
                      marginBottom: 'var(--space-4)',
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)',
                    }}
                  >
                    Project Status
                  </h2>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    {/* Modeling Status */}
                    <div style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}>
                      <label style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        color: 'var(--gray-400)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-medium)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Modeling Status
                      </label>
                      <select
                        value={project.modelingStatus || ''}
                        onChange={handleStatusChange}
                        disabled={updatingStatus}
                        style={{
                          width: '100%',
                          padding: 'var(--space-2) var(--space-3)',
                          backgroundColor: 'var(--dark-elevated)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--gray-100)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                          cursor: updatingStatus ? 'not-allowed' : 'pointer',
                          opacity: updatingStatus ? 0.6 : 1,
                        }}
                      >
                        <option value="">Select Status</option>
                        <option value="Startup">Startup</option>
                        <option value="Data Prep">Data Prep</option>
                        <option value="Utilities">Utilities</option>
                        <option value="Ready to Model">Ready to Model</option>
                        <option value="Modeling">Modeling</option>
                        <option value="Double Check">Double Check</option>
                        <option value="Corrections">Corrections</option>
                        <option value="Review">Review</option>
                        <option value="Ready to Export">Ready to Export</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>

                    {/* Modeling State */}
                    {project.modelingStatus !== 'Completed' && (
                      <div style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}>
                        <label style={{
                          display: 'block',
                          marginBottom: 'var(--space-2)',
                          color: 'var(--gray-400)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-medium)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Modeling State
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                          <div style={{
                            padding: 'var(--space-2) var(--space-3)',
                            backgroundColor: 'var(--dark-elevated)',
                            border: '1px solid var(--gray-700)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--gray-300)',
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-medium)',
                          }}>
                            {project.modelingState || 'Active'}
                          </div>
                          {project.modelingState !== 'Hold' && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setShowHoldModal(true)}
                            >
                              <Pause size={14} />
                              Put on Hold
                            </Button>
                          )}
                          {project.modelingState === 'Hold' && (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={handleRemoveFromHold}
                              disabled={removingHold}
                            >
                              <Play size={14} />
                              {removingHold ? 'Removing...' : 'Remove from Hold'}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Hold Information */}
                    {project.modelingState === 'Hold' && project.holdStartDate && (
                      <div style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: 'var(--radius-lg)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                          <AlertCircle size={16} style={{ color: '#fbbf24' }} />
                          <span style={{ fontWeight: 'var(--font-semibold)', color: '#fbbf24', fontSize: 'var(--text-sm)' }}>
                            Hold Information
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
                          {project.holdReason && (
                            <div>
                              <span style={{ color: '#fbbf24', fontWeight: 'var(--font-medium)' }}>Reason: </span>
                              <span style={{ color: '#fde68a' }}>{formatHoldReason(project.holdReason)}</span>
                            </div>
                          )}
                          {project.holdNotes && (
                            <div>
                              <span style={{ color: '#fbbf24', fontWeight: 'var(--font-medium)' }}>Notes: </span>
                              <span style={{ color: '#fde68a' }}>{project.holdNotes}</span>
                            </div>
                          )}
                          <div>
                            <span style={{ color: '#fbbf24', fontWeight: 'var(--font-medium)' }}>On Hold Since: </span>
                            <span style={{ color: '#fde68a' }}>
                              {formatDate(project.holdStartDate)} ({getDaysOnHold(project.holdStartDate)} days)
                            </span>
                          </div>
                          {project.nextFollowUpDate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                              <Calendar size={14} style={{ color: '#fbbf24' }} />
                              <span style={{ color: '#fbbf24', fontWeight: 'var(--font-medium)' }}>Next Follow-up: </span>
                              <span style={{
                                color: isOverdue(project.nextFollowUpDate) ? '#ef4444' : '#fde68a',
                                fontWeight: isOverdue(project.nextFollowUpDate) ? 'var(--font-semibold)' : 'normal'
                              }}>
                                {formatDate(project.nextFollowUpDate)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Model Planner Section */}
                {(project.modelingStatus === 'Ready to Model' ||
                  project.modelingStatus === 'Modeling' ||
                  project.modelingStatus === 'Corrections' ||
                  project.modelingStatus === 'Review') && (
                  <div>
                    <h2
                      style={{
                        margin: 0,
                        marginBottom: 'var(--space-4)',
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-200)',
                      }}
                    >
                      Model Planner
                    </h2>
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => window.open(`/planner/${project.id}`, '_blank')}
                      style={{ width: '100%' }}
                    >
                      <Clipboard size={16} />
                      Open Model Planner
                    </Button>
                  </div>
                )}

                {/* Client & Contact */}
                <div>
                  <h2
                    style={{
                      margin: 0,
                      marginBottom: 'var(--space-4)',
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)',
                    }}
                  >
                    Client & Contact
                  </h2>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {/* Client */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}>
                      <Building2 size={16} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label style={{
                          display: 'block',
                          marginBottom: 'var(--space-2)',
                          color: 'var(--gray-400)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-medium)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Client
                        </label>
                        <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                          {project.clientName || 'Not Specified'}
                        </div>
                      </div>
                    </div>

                    {/* Contact */}
                    {project.contact && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}>
                        <User size={16} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={{
                            display: 'block',
                            marginBottom: 'var(--space-2)',
                            color: 'var(--gray-400)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-medium)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Contact
                          </label>
                          <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                            {project.contact.name || 'Not Specified'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Email */}
                    {project.contact && project.contact.email && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}>
                        <Mail size={14} style={{ color: 'var(--cta-blue)', marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={{
                            display: 'block',
                            marginBottom: 'var(--space-2)',
                            color: 'var(--gray-400)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-medium)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Email
                          </label>
                          <a
                            href={`mailto:${project.contact.email}`}
                            style={{
                              color: 'var(--cta-blue)',
                              fontSize: 'var(--text-sm)',
                              textDecoration: 'none',
                              wordBreak: 'break-word'
                            }}
                          >
                            {project.contact.email}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Phone */}
                    {project.contact && project.contact.phone_number && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}>
                        <Phone size={14} style={{ color: 'var(--cta-blue)', marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={{
                            display: 'block',
                            marginBottom: 'var(--space-2)',
                            color: 'var(--gray-400)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-medium)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Phone
                          </label>
                          <a
                            href={`tel:${project.contact.phone_number}`}
                            style={{
                              color: 'var(--cta-blue)',
                              fontSize: 'var(--text-sm)',
                              textDecoration: 'none'
                            }}
                          >
                            {project.contact.phone_number}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Date Received */}
                    {project.initial_acceptance && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}>
                        <Calendar size={16} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={{
                            display: 'block',
                            marginBottom: 'var(--space-2)',
                            color: 'var(--gray-400)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-medium)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Date Received
                          </label>
                          <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                            {formatDate(project.initial_acceptance)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Discrepancies Section */}
                <div>
                  <h2
                    style={{
                      margin: 0,
                      marginBottom: 'var(--space-4)',
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)',
                    }}
                  >
                    Discrepancies
                  </h2>

                  {loadingDiscrepancies ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 'var(--space-8)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        border: '3px solid var(--gray-600)',
                        borderTopColor: 'var(--cta-blue)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                    </div>
                  ) : discrepancies.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-6)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                      textAlign: 'center',
                    }}>
                      <MessageSquare size={24} style={{ color: 'var(--gray-500)' }} />
                      <p style={{
                        margin: 0,
                        color: 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                      }}>
                        No discrepancies for this project
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                      {discrepancies.map((discrepancy) => (
                        <div
                          key={discrepancy.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-4)',
                            backgroundColor: 'var(--dark-surface)',
                            border: '1px solid var(--gray-700)',
                            borderRadius: 'var(--radius-lg)',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setSelectedDiscrepancyId(discrepancy.id);
                            setShowDiscrepancyDetailsModal(true);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                            e.currentTarget.style.borderColor = 'var(--gray-600)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--dark-surface)';
                            e.currentTarget.style.borderColor = 'var(--gray-700)';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontSize: 'var(--text-sm)',
                                fontWeight: 'var(--font-medium)',
                                color: 'var(--gray-200)',
                                marginBottom: 'var(--space-1)',
                              }}>
                                {discrepancy.discrepancyType}
                              </div>
                              {discrepancy.description && (
                                <div style={{
                                  fontSize: 'var(--text-xs)',
                                  color: 'var(--gray-400)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {discrepancy.description}
                                </div>
                              )}
                            </div>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--text-xs)',
                                fontWeight: 'var(--font-medium)',
                                color: 'white',
                                backgroundColor: getDiscrepancyStatusColor(discrepancy.discrepancyStatus),
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {discrepancy.discrepancyStatus}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--gray-500)',
                          }}>
                            {discrepancy.createdByName && (
                              <span>Created by {discrepancy.createdByName}</span>
                            )}
                            {discrepancy.createdAt && (
                              <>
                                <span>•</span>
                                <span>{formatDate(discrepancy.createdAt)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Project Information */}
                {(project.model_request_additional_info || project.modelRequestAdditionalInfo ||
                  (project.additional_requests && project.additional_requests.length > 0) ||
                  (project.additionalRequests && project.additionalRequests.length > 0) ||
                  (project.file_formats_needed && project.file_formats_needed.length > 0) ||
                  (project.fileFormatsNeeded && project.fileFormatsNeeded.length > 0) ||
                  (project.template_filter && project.template_filter.length > 0) ||
                  (project.templateFilter && project.templateFilter.length > 0) ||
                  project.erosion_due_date ||
                  project.finish_grade_due_date) && (
                  <div>
                    <h2
                      style={{
                        margin: 0,
                        marginBottom: 'var(--space-4)',
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-200)',
                      }}
                    >
                      Project Information
                    </h2>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                      {/* Additional Information */}
                      {(project.model_request_additional_info || project.modelRequestAdditionalInfo) && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 'var(--space-3)',
                          padding: 'var(--space-4)',
                          backgroundColor: 'var(--dark-surface)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-lg)',
                        }}>
                          <FileText size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={{
                              display: 'block',
                              marginBottom: 'var(--space-2)',
                              color: 'var(--gray-400)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-medium)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Additional Information
                            </label>
                            <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                              {project.model_request_additional_info || project.modelRequestAdditionalInfo}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Additional Requests */}
                      {((project.additional_requests && Array.isArray(project.additional_requests) && project.additional_requests.length > 0) ||
                        (project.additionalRequests && Array.isArray(project.additionalRequests) && project.additionalRequests.length > 0)) && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 'var(--space-3)',
                          padding: 'var(--space-4)',
                          backgroundColor: 'var(--dark-surface)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-lg)',
                        }}>
                          <Package size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={{
                              display: 'block',
                              marginBottom: 'var(--space-2)',
                              color: 'var(--gray-400)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-medium)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Additional Requests
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                              {(project.additional_requests || project.additionalRequests).map((request, index) => (
                                <span
                                  key={index}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 10px',
                                    backgroundColor: 'var(--gray-700)',
                                    color: 'var(--gray-300)',
                                    border: '1px solid var(--gray-600)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: 'var(--font-medium)',
                                  }}
                                >
                                  {request}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* File Formats Needed */}
                      {((project.file_formats_needed && Array.isArray(project.file_formats_needed) && project.file_formats_needed.length > 0) ||
                        (project.fileFormatsNeeded && Array.isArray(project.fileFormatsNeeded) && project.fileFormatsNeeded.length > 0)) && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 'var(--space-3)',
                          padding: 'var(--space-4)',
                          backgroundColor: 'var(--dark-surface)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-lg)',
                        }}>
                          <FileText size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={{
                              display: 'block',
                              marginBottom: 'var(--space-2)',
                              color: 'var(--gray-400)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-medium)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              File Formats Needed
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                              {(project.file_formats_needed || project.fileFormatsNeeded).map((format, index) => (
                                <span
                                  key={index}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 10px',
                                    backgroundColor: 'var(--gray-700)',
                                    color: 'var(--gray-300)',
                                    border: '1px solid var(--gray-600)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: 'var(--font-medium)',
                                  }}
                                >
                                  {format}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Templates */}
                      {((project.template_filter && Array.isArray(project.template_filter) && project.template_filter.length > 0) ||
                        (project.templateFilter && Array.isArray(project.templateFilter) && project.templateFilter.length > 0)) && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 'var(--space-3)',
                          padding: 'var(--space-4)',
                          backgroundColor: 'var(--dark-surface)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-lg)',
                        }}>
                          <Layout size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={{
                              display: 'block',
                              marginBottom: 'var(--space-2)',
                              color: 'var(--gray-400)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-medium)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Templates
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                              {(project.template_filter || project.templateFilter).map((template, index) => (
                                <span
                                  key={index}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 10px',
                                    backgroundColor: 'var(--gray-700)',
                                    color: 'var(--gray-300)',
                                    border: '1px solid var(--gray-600)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: 'var(--font-medium)',
                                  }}
                                >
                                  {template}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* EC Due Date */}
                      {project.erosion_due_date && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 'var(--space-3)',
                          padding: 'var(--space-4)',
                          backgroundColor: 'var(--dark-surface)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-lg)',
                        }}>
                          <Calendar size={16} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={{
                              display: 'block',
                              marginBottom: 'var(--space-2)',
                              color: 'var(--gray-400)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-medium)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              EC Due Date
                            </label>
                            <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                              {formatDate(project.erosion_due_date)}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* FG Due Date */}
                      {project.finish_grade_due_date && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 'var(--space-3)',
                          padding: 'var(--space-4)',
                          backgroundColor: 'var(--dark-surface)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-lg)',
                        }}>
                          <Calendar size={16} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={{
                              display: 'block',
                              marginBottom: 'var(--space-2)',
                              color: 'var(--gray-400)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-medium)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              FG Due Date
                            </label>
                            <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                              {formatDate(project.finish_grade_due_date)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Team Assignment */}
                {project.modelBuilder && (
                  <div>
                    <h2
                      style={{
                        margin: 0,
                        marginBottom: 'var(--space-4)',
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-200)',
                      }}
                    >
                      Team Assignment
                    </h2>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}>
                      <Briefcase size={18} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label style={{
                          display: 'block',
                          marginBottom: 'var(--space-1)',
                          color: 'var(--gray-400)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-medium)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Model Builder
                        </label>
                        <span style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)' }}>
                          {project.modelBuilder.name}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Related Revisions */}
                {relatedRevisions.length > 0 && (
                  <div>
                    <h2
                      style={{
                        margin: 0,
                        marginBottom: 'var(--space-4)',
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-200)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                      }}
                    >
                      Related Revisions
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '20px',
                        padding: '0 6px',
                        backgroundColor: 'var(--gray-700)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}>
                        {relatedRevisions.length}
                      </span>
                    </h2>

                    {loadingRevisions ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 'var(--space-6)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid var(--gray-600)',
                          borderTopColor: 'var(--cta-blue)',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {relatedRevisions.map((revision) => (
                          <div
                            key={revision.id}
                            onClick={() => setSelectedRevision(revision)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 'var(--space-3)',
                              padding: 'var(--space-3)',
                              backgroundColor: 'var(--dark-surface)',
                              border: '1px solid var(--gray-700)',
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                              e.currentTarget.style.borderColor = 'var(--gray-600)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--dark-surface)';
                              e.currentTarget.style.borderColor = 'var(--gray-700)';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
                              <span style={{
                                flexShrink: 0,
                                padding: '2px 8px',
                                backgroundColor: revision.rev === 0 ? 'var(--cta-blue)' : revision.rev === 999 ? 'var(--warning)' : 'var(--gray-600)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--text-xs)',
                                fontWeight: 'var(--font-semibold)',
                                color: 'white',
                              }}>
                                {revision.rev === 0 ? 'Base' : revision.rev === 999 ? 'Erosion' : `Rev ${revision.rev}`}
                              </span>
                              <span style={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--gray-200)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {revision.projectName}
                              </span>
                            </div>
                            <span style={{
                              flexShrink: 0,
                              padding: '2px 8px',
                              backgroundColor: revision.modelingState === 'Hold' ? 'rgba(245, 158, 11, 0.2)' :
                                             revision.modelingStatus === 'Completed' ? 'rgba(16, 185, 129, 0.2)' : 'var(--gray-700)',
                              border: `1px solid ${revision.modelingState === 'Hold' ? 'rgba(245, 158, 11, 0.4)' :
                                                   revision.modelingStatus === 'Completed' ? 'rgba(16, 185, 129, 0.4)' : 'var(--gray-600)'}`,
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--text-xs)',
                              color: revision.modelingState === 'Hold' ? '#fbbf24' :
                                     revision.modelingStatus === 'Completed' ? '#34d399' : 'var(--gray-400)',
                            }}>
                              {revision.modelingState === 'Hold' ? 'On Hold' : revision.modelingStatus}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>

        {/* Bring Files Back - only for completed projects idle 90+ days */}
        {project?.modelingStatus === 'Completed' &&
          project?.lastStageChangeDate &&
          (new Date() - new Date(project.lastStageChangeDate)) / 86400000 > 90 && (
          <ModalFooter>
            <Button
              variant="primary"
              size="sm"
              icon={<RefreshCw size={14} />}
              loading={refreshingFiles}
              onClick={handleBringFilesBack}
              disabled={refreshingFiles}
            >
              Bring Files Back
            </Button>
          </ModalFooter>
        )}
      </Modal>

      {/* Hold Project Modal */}
      <HoldProjectModal
        project={project}
        isOpen={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        onSuccess={handleHoldSuccess}
      />

      {/* Subitem Modal */}
      {showSubitemModal && project && (
        <FullSubitemModal
          project={project}
          isOpen={showSubitemModal}
          onClose={() => setShowSubitemModal(false)}
          onUpdateProject={() => {}} // No-op for read-only view from project details
          employees={[]} // Empty array since we're only viewing
        />
      )}

      {/* Status Advancement Warning Modal */}
      <StatusAdvancementWarningModal
        isOpen={showWarningModal}
        onClose={handleCloseWarning}
        onOverride={handleOverrideWarning}
        incompleteSubitems={warningData.incompleteSubitems}
        currentStatus={warningData.currentStatus}
        targetStatus={warningData.targetStatus}
      />

      {/* Model Planner Wizard Modal */}
      <ModelPlannerWizardModal
        project={project}
        isOpen={showModelPlannerModal}
        onClose={() => setShowModelPlannerModal(false)}
        onOpenDiscrepancyModal={handleOpenDiscrepancyModal}
      />

      {/* Discrepancy Modal */}
      {showDiscrepancyModal && (
        <DiscrepancyModal
          discrepancy={prefilledDiscrepancy}
          isOpen={showDiscrepancyModal}
          isCreating={true}
          onClose={handleDiscrepancyClose}
          onSave={handleDiscrepancySave}
          onDelete={() => {}}
          getStatusColor={() => '#666'}
          getPriorityColor={() => '#666'}
          context="model-planner"
        />
      )}

      {/* Discrepancy Details Modal */}
      {showDiscrepancyDetailsModal && selectedDiscrepancyId && (
        <DiscrepancyDetailsModal
          discrepancyId={selectedDiscrepancyId}
          isOpen={showDiscrepancyDetailsModal}
          onClose={() => {
            setShowDiscrepancyDetailsModal(false);
            setSelectedDiscrepancyId(null);
          }}
        />
      )}

      {/* Related Revision Details Modal */}
      {selectedRevision && (
        <ProjectDetailsModal
          project={selectedRevision}
          isOpen={!!selectedRevision}
          onClose={() => setSelectedRevision(null)}
          onProjectUpdate={onProjectUpdate}
        />
      )}
    </>
  );
};

export default ProjectDetailsModal;
